import type { Stripe, StripeElementLocale } from '@stripe/stripe-js';
import { countryConfig } from '../country';

/**
 * The card form ON OUR SITE (Stripe Elements) — the switch. Kazakhstan since
 * 2026-10-19 (`lib/stripe-kz.ts` on kz-main), Spain since 2026-10-21.
 *
 * A Stripe deployment (`payments.checkout: 'stripe'`) used Stripe's hosted
 * Checkout page; the in-site modal (`StripeCardModal`) is used ONLY when this
 * build carries a Stripe PUBLISHABLE key — `VITE_STRIPE_PUBLISHABLE_KEY`
 * (MiLicita: `deploy/milicita/build-web.sh` reads it from the droplet's
 * `web.env`), or Kazakhstan's original name `VITE_STRIPE_KZ_PK` (its CI
 * variable). Without one, /plans goes to the hosted page exactly as before;
 * that is how the feature ships before the key exists, and how it is rolled
 * back (unset the variable, rebuild).
 *
 * Read at CALL time, not once at import, so a test can stub the env per case.
 *
 * Anything that is not a `pk_…` key reads as «no key»: an empty value, or an
 * unexpanded CI reference (`$STRIPE_KZ_PUBLISHABLE_KEY`) if the variable is
 * ever deleted. A secret key can never get this far — `assertProdEnv` stops
 * the build.
 */
export function stripePublishableKey(): string | null {
  if (countryConfig.payments.checkout !== 'stripe') return null;
  const env = import.meta.env as Record<string, unknown>;
  const raw = env['VITE_STRIPE_PUBLISHABLE_KEY'] || env['VITE_STRIPE_KZ_PK'];
  const key = typeof raw === 'string' ? raw.trim() : '';
  return /^pk_(live|test)_[A-Za-z0-9]+$/.test(key) ? key : null;
}

/** Pay in our own modal (Stripe Elements) instead of leaving for Stripe Checkout. */
export function stripeElementsEnabled(): boolean {
  return stripePublishableKey() !== null;
}

/**
 * The language of Stripe's own strings inside the card fields, for one of our
 * UI locales. Stripe has no Kazakh, so a `kk` buyer gets Russian — what the
 * Kazakh form always did; Spain gets Spanish.
 */
export function stripeElementsLocale(locale: string): StripeElementLocale {
  // Chile (Liciora): Stripe's Latin American Spanish, not Spain's.
  if (locale === 'es' && countryConfig.code === 'cl') return 'es-419';
  return locale === 'es' ? 'es' : 'ru';
}

/**
 * Stripe's zero-decimal currencies — the same list as the server's
 * `stripe-catalog.ts`. Tenge and euro are NOT: 19 900 ₸ is 1 990 000 for
 * Stripe, 49 € is 4 900.
 */
const ZERO_DECIMAL = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);

/**
 * A whole-unit price (`plan.priceUzs` — tenge in Kazakhstan, euros in Spain)
 * → the minor units `<Elements mode: 'subscription'>` takes as `amount`.
 * Mirrors the server's `toMinorUnits`, whose Price check is what actually
 * guards the charge.
 */
export function stripeMinorUnits(amount: number, currency: string): number {
  return ZERO_DECIMAL.has(currency.toLowerCase()) ? Math.round(amount) : Math.round(amount * 100);
}

/** One Stripe.js per page, shared by the /plans preload and every modal opening. */
let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Stripe.js (~200 KB from js.stripe.com), loaded once per page.
 *
 * Lives here, not in `StripeCardModal`, so /plans can START it when the page
 * mounts (`preloadStripeCheckout`) instead of when «Подписаться» is pressed —
 * QA measured 3.7–5.7 s from the press to usable card fields on 19.10, and the
 * script download was one leg of it. The modal then gets the same promise.
 *
 * A blocked js.stripe.com (an ad blocker, a corporate proxy) must not be
 * cached forever: a failed load resolves `null` and clears the promise, so the
 * next opening tries again.
 */
export function getStripe(key: string): Promise<Stripe | null> {
  if (!stripePromise) {
    // `/pure`: Stripe.js is fetched from js.stripe.com only when `loadStripe`
    // is CALLED — the plain entry injects the script into every page that
    // bundles it. Imported here, not at the top, so the prerender (which
    // renders /plans in Node) never evaluates Stripe's loader at all.
    stripePromise = import('@stripe/stripe-js/pure')
      .then(({ loadStripe }) => loadStripe(key))
      .catch(() => null)
      .then((loaded) => {
        if (!loaded) stripePromise = null;
        return loaded;
      });
  }
  return stripePromise;
}

/** Tests only: forget the page's Stripe.js, as a fresh page load would. */
export function resetStripeForTests(): void {
  stripePromise = null;
}

/**
 * Warm the on-site card form while the buyer reads the prices: Stripe.js and
 * the modal's own chunk (`loadModal`, the same `import()` the lazy component
 * uses, so the browser fetches it once). A no-op without a publishable key —
 * the hosted Checkout needs neither, and the Uzbek build never gets here.
 *
 * Started when the browser is idle (or after a short delay where
 * `requestIdleCallback` does not exist — Safari), so it never competes with
 * the page's own hydration. Returns the cancel for an effect cleanup.
 */
export function preloadStripeCheckout(loadModal: () => Promise<unknown>): () => void {
  const key = stripePublishableKey();
  if (!key) return () => undefined;
  const run = () => {
    void getStripe(key);
    // A failed preload is swallowed: the press imports the chunk again.
    void loadModal().catch(() => undefined);
  };
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(run, { timeout: 1500 });
    // Tolerant on purpose: a test (or an odd shim) may provide `requestIdleCallback`
    // without its cancel, and an effect cleanup must never throw.
    return () => {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(id);
    };
  }
  const id = window.setTimeout(run, 200);
  return () => window.clearTimeout(id);
}
